import {
	Id,
	LaravelResponse,
	ModelUsageResource,
	keyToUrl,
	withDestroyEndpoint,
	withIndexEndpoint,
	withOptionsEndpoint,
	withShowEndpoint,
	withStoreEndpoint,
	withUpdateEndpoint,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosRequestConfig } from "axios";
import { DataFieldIndexResource, DataFieldOptionResource } from "../../../../shared/types/dataField";
import {
	DataFieldOptionIndexResource,
	DataFieldOptionOrderIndexResource,
	DataFieldOptionSaveRequest,
	DataFieldOptionUpdateResource,
} from "../../../../shared/types/dataFieldOption";
import { DataFieldSaveRequest, DataFieldUpdateResource } from "../../shared/types/dataField";

const baseKey = "data-fields";
const indexByContactViewKey = [baseKey, "by-contact-view"];
const showUsageKey = (id: Id) => [baseKey, `${id}`, "usage"];

export const dataFieldService = {
	...withIndexEndpoint<DataFieldIndexResource>([baseKey]),
	...withShowEndpoint<DataFieldUpdateResource>([baseKey]),
	...withDestroyEndpoint([baseKey]),
	...withStoreEndpoint<DataFieldSaveRequest>([baseKey]),
	...withUpdateEndpoint<DataFieldSaveRequest>([baseKey]),
	...withOptionsEndpoint<DataFieldOptionResource>([baseKey]),
	indexAll: (config?: AxiosRequestConfig) =>
		axios.get<LaravelResponse<DataFieldIndexResource[]>>(keyToUrl([baseKey, "all"]), config),
	indexAllKey: [baseKey, "all"],
	indexByContactViewKey,
	indexByContactView: (config?: AxiosRequestConfig) =>
		axios.get<LaravelResponse<DataFieldIndexResource[]>>(keyToUrl(indexByContactViewKey), config),
	order: (dataFieldId: Id, language: string, dataFieldOptionIds: Id[]) =>
		axios.put(
			keyToUrl([baseKey, `${dataFieldId}`, "order-options"]),
			{
				dataFieldOptionIds,
			},
			{ headers: { "Content-Language": language } },
		),
	showUsageKey,
	showUsage: (id: Id) => axios.get<LaravelResponse<ModelUsageResource[]>>(keyToUrl(showUsageKey(id))),
};

export const createDataFieldOptionService = (id: Id) => {
	const optionsBaseKey = ["data-fields", `${id}`, "options"];

	return {
		...withIndexEndpoint<DataFieldOptionIndexResource>(optionsBaseKey),
		...withShowEndpoint<DataFieldOptionUpdateResource>(optionsBaseKey),
		...withDestroyEndpoint(optionsBaseKey),
		...withStoreEndpoint<DataFieldOptionSaveRequest>(optionsBaseKey),
		...withUpdateEndpoint<DataFieldOptionSaveRequest>(optionsBaseKey),
		...withOptionsEndpoint<DataFieldOptionIndexResource>(optionsBaseKey),
		listByLanguage: (language: string) =>
			axios.get<LaravelResponse<DataFieldOptionOrderIndexResource[]>>(keyToUrl(optionsBaseKey), {
				headers: { "Content-Language": language },
			}),
	};
};

const dataFields: DataFieldIndexResource[] = [];

const dataFieldsFetcher = (config?: AxiosRequestConfig) => () =>
	dataFieldService.indexAll(config).then((r) => r.data.data);

export const useDataFieldsQuery = (params?: Record<string, string | number>) => {
	const { data, isLoading } = useQuery({
		queryKey: dataFieldService.indexAllKey.concat(JSON.stringify(params || {})),
		queryFn: dataFieldsFetcher({ params }),
	});

	return {
		data: data || dataFields,
		isLoading,
	};
};
